import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Globe, Users, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { API_URL } from '../config/api';

/**
 * Modal shown when the user taps "Share" at the end of a walk/run.
 * Lets the user pick:
 *   • Public (visible in the main feed only)
 *   • One or more joined challenges (primary target = challenge feed)
 * The selected `challenge_ids` array is returned via `onConfirm(ids)`.
 * Empty array means "publish public only".
 */
export default function SharePostModal({ open, onClose, onConfirm, publishing = false }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [challenges, setChallenges] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [scope, setScope] = useState('public'); // 'public' | 'challenges'

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSelected(new Set());
    setScope('public');
    (async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await axios.get(`${API_URL}/api/challenges`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        if (!cancelled) setChallenges(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        if (!cancelled) setChallenges([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggleChallenge = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (scope === 'public') {
      onConfirm([]); // no challenge → public only
    } else {
      onConfirm(Array.from(selected));
    }
  };

  if (!open) return null;

  const canConfirm = !publishing && (scope === 'public' || selected.size > 0);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000] p-4"
      onClick={() => !publishing && onClose()}
      data-testid="share-post-overlay"
    >
      <div
        className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl flex flex-col ${
          isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
        }`}
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-5 py-4 flex items-center justify-between border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('share.title', 'Dove vuoi pubblicare?')}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={publishing}
            className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
            data-testid="share-post-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-2 overflow-y-auto">
          {/* Scope: public */}
          <button
            type="button"
            onClick={() => setScope('public')}
            className={`w-full p-4 rounded-2xl flex items-center gap-3 border transition-colors ${
              scope === 'public'
                ? (isDark ? 'bg-[#E8334A]/15 border-[#E8334A] text-white' : 'bg-[#E8334A]/10 border-[#E8334A] text-gray-900')
                : (isDark ? 'bg-white/5 border-white/10 text-white/80' : 'bg-gray-50 border-gray-200 text-gray-800')
            }`}
            data-testid="share-scope-public"
          >
            <Globe className={`w-5 h-5 ${scope === 'public' ? 'text-[#E8334A]' : (isDark ? 'text-white/60' : 'text-gray-500')}`} />
            <div className="flex-1 text-left">
              <div className="font-medium">{t('share.public', 'Pubblico')}</div>
              <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('share.publicDesc', 'Visibile a tutti nel feed principale')}
              </div>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              scope === 'public' ? 'border-[#E8334A] bg-[#E8334A]' : (isDark ? 'border-white/30' : 'border-gray-300')
            }`}>
              {scope === 'public' && <div className="w-2 h-2 rounded-full bg-white" />}
            </div>
          </button>

          {/* Scope: challenges */}
          <button
            type="button"
            onClick={() => setScope('challenges')}
            className={`w-full p-4 rounded-2xl flex items-center gap-3 border transition-colors ${
              scope === 'challenges'
                ? (isDark ? 'bg-[#E8334A]/15 border-[#E8334A] text-white' : 'bg-[#E8334A]/10 border-[#E8334A] text-gray-900')
                : (isDark ? 'bg-white/5 border-white/10 text-white/80' : 'bg-gray-50 border-gray-200 text-gray-800')
            }`}
            data-testid="share-scope-challenges"
          >
            <Users className={`w-5 h-5 ${scope === 'challenges' ? 'text-[#E8334A]' : (isDark ? 'text-white/60' : 'text-gray-500')}`} />
            <div className="flex-1 text-left">
              <div className="font-medium">{t('share.inChallenges', 'Nelle sfide')}</div>
              <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('share.inChallengesDesc', 'Visibile solo ai partecipanti delle sfide selezionate')}
              </div>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              scope === 'challenges' ? 'border-[#E8334A] bg-[#E8334A]' : (isDark ? 'border-white/30' : 'border-gray-300')
            }`}>
              {scope === 'challenges' && <div className="w-2 h-2 rounded-full bg-white" />}
            </div>
          </button>

          {/* Challenges list — only shown when 'challenges' scope is active */}
          {scope === 'challenges' && (
            <div className={`mt-3 rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              {loading ? (
                <div className="py-8 flex justify-center">
                  <Loader2 className={`w-5 h-5 animate-spin ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
                </div>
              ) : challenges.length === 0 ? (
                <p className={`text-center text-sm py-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t('share.noChallenges', 'Non partecipi ad alcuna sfida')}
                </p>
              ) : (
                challenges.map((c) => (
                  <label
                    key={c.challenge_id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                      isDark ? 'hover:bg-white/5' : 'hover:bg-white'
                    }`}
                    data-testid={`share-challenge-${c.challenge_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(c.challenge_id)}
                      onChange={() => toggleChallenge(c.challenge_id)}
                      className="w-5 h-5 rounded accent-[#E8334A]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {c.name || c.title || t('share.unnamed', 'Sfida')}
                      </div>
                      {c.end_date && (
                        <div className={`text-[11px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                          {t('share.until', 'Fino al')} {new Date(c.end_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        <div className={`p-4 flex items-center gap-2 border-t flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <button
            type="button"
            onClick={onClose}
            disabled={publishing}
            className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
            data-testid="share-cancel"
          >
            {t('common.cancel', 'Annulla')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-1 py-3 rounded-xl font-semibold bg-[#E8334A] hover:bg-[#d42a40] text-white flex items-center justify-center gap-2 shadow-lg shadow-[#E8334A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="share-confirm"
          >
            {publishing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : t('share.publish', 'Pubblica')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
